import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  House, Users, Clock, CalendarBlank, Buildings, Bell, SignOut, UserCircle,
  MapPinArea, CalendarStar, CaretDown, Briefcase, Kanban, CurrencyInr, Receipt,
  ChartLine, Coins, Target, ClipboardText, ShieldCheck, Wallet, Coffee,
  Ticket, Package, GraduationCap, ChartBar, UserCirclePlus, ChartLineUp, Star,
  Gift, MegaphoneSimple, UserPlus, TrendUp, Question, Gear,
  TreeStructure, UsersThree, ListChecks, CalendarCheck, Warning, LockKey, Calculator,
  EnvelopeSimple, PaintBrush,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { SHELL } from "../constants/testIds";
import AskZenith from "./AskZenith";
import TrialCountdownStrip from "./TrialCountdownStrip";
import SetupBanner from "./SetupBanner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";

function roleLabel(role) {
  return {
    super_admin: "Agency · Super Admin",
    company_admin: "Company Admin",
    hr: "HR",
    manager: "Reporting Manager",
    employee: "Employee",
  }[role] || role;
}

const ROLES_ALL = ["super_admin", "company_admin", "hr", "manager", "employee"];
const ROLES_HR = ["super_admin", "company_admin", "hr", "manager"];
const ROLES_ADMIN = ["super_admin", "company_admin", "hr"];
const ROLES_EMP = ["employee", "hr", "manager"];

const NAV_SECTIONS = [
  {
    label: null,
    items: [
      { to: "/app", icon: House, label: "Dashboard", testid: SHELL.navDashboard, end: true, roles: ROLES_ALL },
      { to: "/app/tasks", icon: ListChecks, label: "Tasks", testid: "nav-tasks", roles: ROLES_ALL },
      { to: "/app/approvals-inbox", icon: Clock, label: "Approvals inbox", testid: "nav-approvals-inbox", roles: ROLES_HR },
      { to: "/app/reports", icon: ChartBar, label: "Reports & analytics", testid: "nav-reports", roles: ROLES_HR },
      { to: "/app/exit-dashboard", icon: TrendUp, label: "Exit dashboard", testid: "nav-exit", roles: ROLES_ADMIN },
      { to: "/app/help", icon: Question, label: "Playbook", testid: "nav-help", roles: ROLES_ALL },
    ],
  },
  {
    label: "People",
    items: [
      { to: "/app/employees", icon: Users, label: "Employees", testid: SHELL.navEmployees, roles: ROLES_HR },
      { to: "/app/bench", icon: Coffee, label: "Bench", testid: "nav-bench", roles: ROLES_HR },
    ],
  },
  {
    label: "Workforce",
    items: [
      { to: "/app/clients", icon: Buildings, label: "Clients", testid: "nav-clients", roles: ROLES_HR },
      { to: "/app/projects", icon: Briefcase, label: "Projects", testid: "nav-projects", roles: ROLES_HR },
      { to: "/app/allocations", icon: Kanban, label: "Allocations", testid: "nav-allocations", roles: ROLES_HR },
    ],
  },
  {
    label: "Time",
    items: [
      { to: "/app/attendance", icon: Clock, label: "Attendance", testid: SHELL.navAttendance, roles: ROLES_HR },
      { to: "/app/checkin", icon: MapPinArea, label: "Check-in", testid: SHELL.navCheckin, roles: ROLES_EMP },
      { to: "/app/regularization", icon: ClipboardText, label: "Regularization", testid: "nav-regularization", roles: ROLES_ALL },
      { to: "/app/shifts", icon: Clock, label: "Shifts & Rosters", testid: "nav-shifts", roles: ROLES_HR },
      { to: "/app/timesheets", icon: ClipboardText, label: "My timesheet", testid: "nav-timesheets", roles: ROLES_EMP },
      { to: "/app/timesheet-approvals", icon: ClipboardText, label: "Approvals", testid: "nav-ts-approvals", roles: ROLES_HR },
      { to: "/app/compliance", icon: ShieldCheck, label: "Compliance", testid: "nav-compliance", roles: ROLES_ALL },
      { to: "/app/leaves", icon: CalendarBlank, label: "Leaves", testid: SHELL.navLeaves, roles: ROLES_ALL },
      { to: "/app/holidays", icon: CalendarStar, label: "Holidays", testid: SHELL.navHolidays, roles: ROLES_ALL },
    ],
  },
  {
    label: "Payroll",
    items: [
      { to: "/app/payroll", icon: CurrencyInr, label: "Payroll runs", testid: "nav-payroll", roles: ROLES_ADMIN },
      { to: "/app/payroll-register", icon: ListChecks, label: "Payroll register", testid: "nav-payroll-register", roles: ROLES_ADMIN },
      { to: "/app/statutory", icon: Receipt, label: "Statutory", testid: "nav-statutory", roles: ROLES_ADMIN },
      { to: "/app/connectors", icon: Receipt, label: "PF/ESIC connectors", testid: "nav-connectors", roles: ROLES_ADMIN },
      { to: "/app/payslips", icon: Wallet, label: "My payslips", testid: "nav-payslips", roles: ROLES_EMP },
      { to: "/app/reimbursements", icon: Receipt, label: "Reimbursements", testid: "nav-reimbursements", roles: ROLES_ALL },
      { to: "/app/trips", icon: MapPinArea, label: "GPS trips (fuel)", testid: "nav-trips", roles: ROLES_ALL },
      { to: "/app/trip-review", icon: MapPinArea, label: "Trip review (HR)", testid: "nav-trip-review", roles: ROLES_ADMIN },
      { to: "/app/reimbursement-policies", icon: Receipt, label: "TA/DA policies", testid: "nav-reimb-policies", roles: ROLES_ADMIN },
      { to: "/app/tax-dashboard", icon: Calculator, label: "Tax dashboard", testid: "nav-tax-dashboard", roles: ROLES_ADMIN },
      { to: "/app/payroll-admin", icon: CurrencyInr, label: "Payroll ops", testid: "nav-payroll-admin", roles: ROLES_ADMIN },
    ],
  },
  {
    label: "Company",
    items: [
      { to: "/app/company", icon: Buildings, label: "Company hub", testid: "nav-company", roles: ROLES_ALL },
      { to: "/app/org-chart", icon: TreeStructure, label: "Org chart", testid: "nav-org-chart", roles: ROLES_ALL },
      { to: "/app/my-team", icon: UsersThree, label: "My team", testid: "nav-my-team", roles: ROLES_ALL },
    ],
  },
  {
    label: "Governance",
    items: [
      { to: "/app/compliance-calendar", icon: CalendarCheck, label: "Compliance calendar", testid: "nav-compliance-cal", roles: ROLES_ADMIN },
      { to: "/app/audit-log", icon: Warning, label: "Audit log", testid: "nav-audit-log", roles: ROLES_ADMIN },
    ],
  },
  {
    label: "Billing",
    items: [
      { to: "/app/invoicing", icon: ChartLine, label: "Invoice data", testid: "nav-invoicing", roles: ROLES_HR },
      { to: "/app/invoicing-pipeline", icon: ChartLine, label: "Invoice pipeline", testid: "nav-invoicing-pipeline", roles: ROLES_HR },
      { to: "/app/billability", icon: ChartLine, label: "Billability & utilization", testid: "nav-billability", roles: ROLES_HR },
      { to: "/app/stat-compliance", icon: ShieldCheck, label: "Statutory (PF · ESIC · TDS)", testid: "nav-stat-compliance", roles: ROLES_HR },
    ],
  },
  {
    label: "Talent",
    items: [
      { to: "/app/recruitment", icon: Coins, label: "Recruitment", testid: "nav-recruitment", roles: ROLES_HR },
      { to: "/app/performance", icon: Target, label: "Performance", testid: "nav-performance", roles: ROLES_ALL },
      { to: "/app/performance-advanced", icon: ChartLineUp, label: "KRA · 360 · 9-box", testid: "nav-performance-adv", roles: ROLES_ALL },
      { to: "/app/okr", icon: Target, label: "OKR — Cascading", testid: "nav-okr", roles: ROLES_ALL },
      { to: "/app/onboarding", icon: UserCirclePlus, label: "Onboarding & docs", testid: "nav-onboarding", roles: ROLES_ALL },
      { to: "/app/surveys", icon: ChartBar, label: "Surveys & eNPS", testid: "nav-surveys", roles: ROLES_ALL },
      { to: "/app/operations", icon: Ticket, label: "Helpdesk · Assets · L&D", testid: "nav-operations", roles: ROLES_ALL },
      { to: "/app/kudos", icon: Star, label: "Kudos & recognition", testid: "nav-kudos", roles: ROLES_ALL },
      { to: "/app/rewards", icon: Gift, label: "Rewards catalogue", testid: "nav-rewards", roles: ROLES_ALL },
      { to: "/app/announcements", icon: MegaphoneSimple, label: "Announcements", testid: "nav-announcements", roles: ROLES_ALL },
      { to: "/app/referrals", icon: UserPlus, label: "Referrals", testid: "nav-referrals", roles: ROLES_ALL },
      { to: "/app/admin", icon: Gear, label: "Admin settings", testid: "nav-admin", roles: ROLES_ADMIN },
      { to: "/app/access", icon: ShieldCheck, label: "Users & roles", testid: "nav-access", roles: ROLES_ADMIN },
      { to: "/app/letters", icon: ClipboardText, label: "HR letters", testid: "nav-letters", roles: ROLES_ALL },
      { to: "/app/bulk-letters", icon: EnvelopeSimple, label: "Bulk letters & email", testid: "nav-bulk-letters", roles: ROLES_HR },
      { to: "/app/letter-studio", icon: PaintBrush, label: "Letter template studio", testid: "nav-letter-studio", roles: ROLES_ADMIN },
      { to: "/app/increment-cycles", icon: TrendUp, label: "Increment cycles", testid: "nav-increment-cycles", roles: ROLES_HR },
    ],
  },
  {
    label: "AI & Insights",
    items: [
      { to: "/app/ai-studio", icon: Star, label: "AI Studio · JD/KRA/KPI", testid: "nav-ai-studio", roles: ROLES_HR, feature: "ai_studio" },
      { to: "/app/kpi-dashboard", icon: ChartLineUp, label: "KPI scoring", testid: "nav-kpi-dashboard", roles: ROLES_HR, feature: "kpi_dashboard" },
    ],
  },
  {
    label: "Learn & Hire",
    items: [
      { to: "/app/learning", icon: GraduationCap, label: "Learning · LMS", testid: "nav-learning", roles: ROLES_ALL, feature: "lms" },
      { to: "/app/ats", icon: UserPlus, label: "ATS · Recruitment", testid: "nav-ats", roles: ROLES_HR, feature: "ats" },
    ],
  },
  {
    label: "Sales",
    items: [
      { to: "/app/sales", icon: TrendUp, label: "Sales console", testid: "nav-sales", roles: ["super_admin"] },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/app/profile", icon: UserCircle, label: "My profile", testid: SHELL.navProfile, roles: ROLES_ALL },
      { to: "/app/tenants", icon: Buildings, label: "Companies", testid: SHELL.navTenants, roles: ["super_admin"] },
      { to: "/app/billing", icon: CurrencyInr, label: "Billing & subscription", testid: "nav-billing", roles: ["super_admin", "company_admin"] },
    ],
  },
];

function NavItem({ to, icon: Icon, label, testid, end, feature }) {
  const { hasFeature, features } = useAuth();
  const featuresLoaded = features && features.size > 0;
  const locked = feature && featuresLoaded && !hasFeature(feature);
  if (locked) {
    return (
      <div
        data-testid={`${testid}-locked`}
        title={`Upgrade your pack to unlock ${label}`}
        className="group flex items-center gap-2.5 px-3 py-1.5 rounded-sm text-sm border-l-2 border-transparent text-white/35 cursor-not-allowed select-none"
      >
        <Icon size={16} weight="duotone" />
        <span className="truncate flex-1">{label}</span>
        <LockKey size={11} weight="fill" className="text-zenith-magenta/70" />
      </div>
    );
  }
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex items-center gap-2.5 px-3 py-1.5 rounded-sm text-sm transition-colors duration-150 border-l-2 ${
          isActive
            ? "bg-white/10 text-white border-adb-green font-semibold"
            : "text-white/70 hover:text-white hover:bg-white/5 border-transparent"
        }`
      }
    >
      <Icon size={16} weight="duotone" />
      <span>{label}</span>
    </NavLink>
  );
}

function TenantSwitcher() {
  const { user, tenantId, setTenantId } = useAuth();
  const [tenants, setTenants] = useState([]);
  useEffect(() => {
    if (user?.role === "super_admin") api.get("/tenants").then(({ data }) => setTenants(data)).catch(() => {});
  }, [user?.role]);

  if (user?.role === "super_admin") {
    const selected = tenants.find((t) => t.id === tenantId);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid={SHELL.tenantSwitcher} className="flex items-center gap-2 border border-zinc-200 rounded-sm px-3 py-1.5 text-sm bg-white hover:bg-zinc-50 transition-colors duration-150">
            <Buildings size={16} weight="duotone" className="text-emerald-700" />
            <span className="font-medium text-zinc-800">{selected ? selected.name : "All companies"}</span>
            <CaretDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel>Switch tenant</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setTenantId(null)} data-testid="tenant-switch-all">All companies (agency view)</DropdownMenuItem>
          {tenants.map((t) => (
            <DropdownMenuItem key={t.id} onSelect={() => setTenantId(t.id)} data-testid={`tenant-switch-${t.slug}`}>{t.name}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  if (user?.tenant) {
    return (
      <div className="flex items-center gap-2 border border-zinc-200 rounded-sm px-3 py-1.5 text-sm bg-white" data-testid={SHELL.tenantSwitcher}>
        <Buildings size={16} weight="duotone" className="text-emerald-700" />
        <span className="font-medium text-zinc-800">{user.tenant.name}</span>
      </div>
    );
  }
  return null;
}

function NotificationBell() {
  const [items, setItems] = useState([]);
  const unread = items.filter((n) => !n.read).length;
  const load = () => api.get("/notifications").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={SHELL.notifBell} className="relative p-2 rounded-sm hover:bg-zinc-100 transition-colors duration-150">
          <Bell size={20} weight="duotone" className="text-zinc-700" />
          {unread > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-orange-500 text-white text-[10px] font-semibold grid place-items-center">{unread}</span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-2 py-1.5">
          <DropdownMenuLabel className="p-0">Notifications</DropdownMenuLabel>
          {unread > 0 && <button className="text-xs text-emerald-700 hover:underline" onClick={markAll} data-testid="notif-mark-all">Mark all as read</button>}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-auto">
          {items.length === 0 && <div className="p-6 text-center text-sm text-zinc-500">Nothing new. You're all caught up.</div>}
          {items.map((n) => (
            <div key={n.id} data-testid={`notif-item-${n.id}`} className={`px-3 py-2 border-t border-zinc-100 ${n.read ? "" : "bg-emerald-50/40"}`}>
              <div className="text-sm font-medium text-zinc-900">{n.title}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{n.body}</div>
              <div className="text-[10px] text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell() {
  const { user, tenantId, logout } = useAuth();
  const navigate = useNavigate();
  const [branding, setBranding] = useState(null);

  useEffect(() => {
    // Fetch tenant branding when a tenant is scoped
    const hasScope = user && (user.tenant_id || tenantId);
    if (!hasScope) { setBranding(null); return; }
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/admin/branding", { params }).then(({ data }) => setBranding(data)).catch(() => setBranding(null));
  }, [user?.tenant_id, tenantId, user?.role]);

  const sections = useMemo(() => {
    return NAV_SECTIONS.map((s) => ({
      ...s, items: s.items.filter((it) => it.roles.includes(user?.role)),
    })).filter((s) => s.items.length > 0);
  }, [user?.role]);

  if (!user) return null;

  const logoUrl = branding?.logo_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${branding.logo_path}` : null;

  return (
    <div className="min-h-screen bg-zinc-50">
      <aside data-testid={SHELL.sidebar} className="fixed inset-y-0 left-0 w-64 border-r border-white/5 orbit-sidebar text-white z-30 hidden md:flex md:flex-col overflow-hidden">
        <div className="absolute inset-0 orbit-grain-dark pointer-events-none opacity-70"></div>
        <div className="absolute -left-16 top-1/3 w-56 h-56 rounded-full bg-adb-green/10 blur-3xl pointer-events-none"></div>
        <Link to="/app" className="relative px-5 py-4 border-b border-white/10 flex items-center gap-2.5 shrink-0">
          {logoUrl ? (
            <div className="w-11 h-11 border border-white/20 rounded-sm bg-white p-1 grid place-items-center shrink-0" data-testid="brand-mark-logo">
              <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" />
            </div>
          ) : (
            <div className="w-12 h-12 rounded-md bg-white p-1 grid place-items-center shrink-0" data-testid="brand-mark">
              <img src="/zenith360-mark.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display text-lg font-bold tracking-tight leading-none truncate">
              {logoUrl ? <span className="text-white">Zenith360 HR</span> : <>Zenith<span className="text-zenith-green">360</span> HR</>}
            </div>
            <div className="text-[9px] uppercase tracking-widest text-white/50 mt-0.5 truncate">
              {logoUrl ? "on " + (branding?.tagline || "your workspace") : "Your complete HR Ecosystem"}
            </div>
          </div>
        </Link>
        <nav className="relative flex-1 py-2 overflow-y-auto">
          {sections.map((s, i) => (
            <div key={i} className="px-2 mb-3">
              {/* Sidebar section headers cycle through logo colours (teal / magenta / green / blue) — restarts on 4th section */}
              {s.label && (() => {
                const zenithSectionColors = ["text-zenith-teal", "text-zenith-magenta", "text-zenith-green", "text-zenith-blue-500"];
                const cls = zenithSectionColors[i % 4];
                return (
                  <div className={`px-2 text-[9px] uppercase tracking-[0.15em] font-semibold ${cls} py-1.5 flex items-center gap-1.5`}>
                    <span className="w-1 h-1 rounded-full bg-current"></span>{s.label}
                  </div>
                );
              })()}
              <div className="space-y-0.5">
                {s.items.map((it) => <NavItem key={it.to} {...it} />)}
              </div>
            </div>
          ))}
        </nav>
        <div className="relative p-3 border-t border-white/10 shrink-0 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/50">Signed in</div>
            <div className="text-sm font-medium text-white truncate">{user.name}</div>
            <div className="text-xs text-white/60 truncate">{user.email}</div>
            <Badge variant="outline" className="mt-1 border-adb-green/40 text-adb-green bg-adb-green/10 rounded-sm">{roleLabel(user.role)}</Badge>
          </div>
          <div className="block pt-2 border-t border-white/10" data-testid="footer-brand-mark">
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-adb-green shrink-0"></div>
              <div className="font-display text-xs font-bold text-white tracking-tight">Zenith360 HR</div>
            </div>
            <div className="text-[9px] italic text-white/50 mt-0.5">Your complete HR Ecosystem</div>
          </div>
        </div>
      </aside>

      <header data-testid={SHELL.topbar} className="fixed top-0 left-0 right-0 md:left-64 h-14 z-20 bg-white/90 backdrop-blur border-b border-zinc-200 flex items-center gap-3 px-4">
        <div className="md:hidden font-display font-bold text-lg flex items-center gap-2">
          {logoUrl ? (
            <div className="w-7 h-7 rounded-sm bg-white border border-zinc-200 p-0.5 grid place-items-center">
              <img src={logoUrl} alt="logo" className="max-w-full max-h-full object-contain" />
            </div>
          ) : (
            <div className="w-8 h-8 rounded-sm bg-white border border-zinc-200 p-0.5 grid place-items-center">
              <img src="/zenith360-mark.png" alt="Zenith360 HR" className="max-w-full max-h-full object-contain" />
            </div>
          )}
          <span className="text-adb-navy">Zenith360 HR</span>
        </div>
        <div className="flex-1" />
        <TenantSwitcher />
        <NotificationBell />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={SHELL.userMenu} className="flex items-center gap-2 p-1 rounded-sm hover:bg-zinc-100">
              <div className="w-8 h-8 rounded-full bg-emerald-700 text-white grid place-items-center text-sm font-semibold">
                {(user.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              <div className="text-sm">{user.name}</div>
              <div className="text-xs text-zinc-500">{user.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate("/app/profile")}>My profile</DropdownMenuItem>
            <DropdownMenuItem onSelect={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn">
              <SignOut size={16} className="mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <main className="md:pl-64 pt-14 min-h-screen">
        <TrialCountdownStrip />
        <div className="p-6 fade-up">
          <SetupBanner />
          <Outlet />
        </div>
      </main>
      <AskZenith />
    </div>
  );
}
